import { PageAttr } from "../../../../../../shared/domain/entities";
import React, { useCallback, useMemo, useState } from "react";
import { SelectComponentProps, SelectProps } from "./types";
import {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";

export const withSelectOptions = <
  T extends Record<string, unknown>,
  V extends Record<string, unknown>
>(
  Component: React.FC<SelectComponentProps<T>>,
  useFetchHook: (
    filters: V,
    config?: UseInfiniteQueryOptions<PageAttr<T>>
  ) => UseInfiniteQueryResult<PageAttr<T>> & {
    allPages: PageAttr<T>["data"];
  },
  param: keyof V
): React.FC<SelectProps<T>> => {
  const ComponentWrapper = (props: SelectProps<T>): React.ReactElement => {
    const {
      selectedValue,
      keyProp,
      customKeyProp,
      multiselect,
      onChangeValue,
    } = props;

    const [keyword, setKeyword] = useState<string>("");

    const {
      allPages,
      isLoading,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } = useFetchHook(keyword === "" ? {} : { [param]: keyword });

    const handleSetKeyword = useCallback((value: string): void => {
      setKeyword(value);
    }, []);

    const finalSelectedValue = useMemo(
      () =>
        Array.isArray(selectedValue)
          ? selectedValue
          : typeof selectedValue === "object"
          ? selectedValue
          : allPages.find((i) => {
              if (customKeyProp) {
                return customKeyProp(i) === selectedValue;
              }

              return i[keyProp] === selectedValue;
            }),
      [allPages, customKeyProp, keyProp, selectedValue]
    );

    const handleOnChange = useCallback(
      (data?: Array<T> | T) => {
        if (multiselect) onChangeValue(data);
        else onChangeValue(data);
      },
      [multiselect, onChangeValue]
    );

    return (
      <Component
        {...props}
        selectedValue={finalSelectedValue}
        handleSetKeyword={handleSetKeyword}
        onChangeValue={handleOnChange}
        isLoading={isLoading || isFetchingNextPage}
        data={allPages}
        getNextPage={fetchNextPage}
        noMore={!hasNextPage}
        keyword={keyword}
      />
    );
  };

  return ComponentWrapper;
};
